/* ↗ .cursor/references/code-notes/r-slide-media.js.md#§1 */

const TEMPLATE_SELECTOR = 'template[data-r-slide-media]';
const SLIDE = 'slideshow-slide';
const SCROLLER = 'slideshow-slides';

/**
 * Replaces every parked template in a slide with its real markup. Idempotent: a hydrated
 * slide has no templates left, so re-entry costs one failed querySelectorAll.
 *
 * `r-img.js` picks the new images up through its own MutationObserver, so the reveal
 * crossfade and the stall watchdog apply to them exactly as they do to server-rendered
 * ones — nothing here needs to know about either.
 *
 * @param {Element | null | undefined} slide
 */
function hydrate(slide) {
  if (!slide) return;

  for (const template of slide.querySelectorAll(TEMPLATE_SELECTOR)) swap(template);
}

/**
 * @param {Element} template
 */
function swap(template) {
  if (template instanceof HTMLTemplateElement) template.replaceWith(template.content);
}

/**
 * The next or previous slide element.
 * @param {Element} slide
 * @param {1 | -1} direction
 * @returns {Element | null}
 */
function sibling(slide, direction) {
  let node = direction === 1 ? slide.nextElementSibling : slide.previousElementSibling;

  // ↗ .cursor/references/code-notes/r-slide-media.js.md#§2
  while (node && (node.localName !== SLIDE || node.hasAttribute('hidden'))) {
    node = direction === 1 ? node.nextElementSibling : node.previousElementSibling;
  }

  return node;
}

/**
 * Hydrates a slide and the one either side of it. One ahead is the whole budget: the
 * shopper can only ever be looking at one slide, and every way of reaching the next one
 * passes through a signal this module listens to.
 * @param {Element | null | undefined} slide
 */
function hydrateNeighbourhood(slide) {
  if (!slide || slide.localName !== SLIDE) return;

  hydrate(slide);
  hydrate(sibling(slide, 1));
  hydrate(sibling(slide, -1));
}

/**
 * The slide a scroller is currently showing. Falls back to the first slide for a
 * slideshow that has not been set up yet (the attribute is written by `slideshow.js`).
 * @param {Element} scroller
 * @returns {Element | null}
 */
function activeSlide(scroller) {
  return scroller.querySelector(`:scope > ${SLIDE}[aria-hidden="false"]`) ?? scroller.firstElementChild;
}

/**
 * @param {Element} root
 */
function hydrateWithin(root) {
  // Cheap early-out: most pointer traffic is over cards that are already hydrated, or
  // over surfaces that never deferred anything in the first place.
  if (!root.querySelector(TEMPLATE_SELECTOR)) return;

  for (const scroller of root.querySelectorAll(SCROLLER)) {
    hydrateNeighbourhood(activeSlide(scroller));
  }

  /* Deferred media outside any slide has no neighbourhood and no commit signal — nothing
     pages to it, so engaging the card IS the signal. */
  for (const template of root.querySelectorAll(TEMPLATE_SELECTOR)) {
    if (!template.closest(SLIDE)) swap(template);
  }
}

/* ↗ .cursor/references/code-notes/r-slide-media.js.md#§3 */

const HOVER_HOST = '[data-r-hover-media]';
const HOVER_CARD = '.card-gallery, .resource-card__media';
const HOVER_LIVE = 'img.product-media__image, img.resource-card__image';
const canHover = window.matchMedia?.('(hover: hover) and (pointer: fine)');

/**
 * @param {Element} host
 */
function buildHoverImage(host) {
  const url = host.getAttribute('data-r-hover-media');
  if (!url) return;
  host.removeAttribute('data-r-hover-media');

  const card = host.closest(HOVER_CARD) ?? host.parentElement;
  const live = card?.querySelector(HOVER_LIVE);
  const join = url.includes('?') ? '&' : '?';
  const widths = live?.getAttribute('srcset')?.match(/\s(\d+)w/g)?.map((w) => w.trim().slice(0, -1)) ?? [];

  const img = document.createElement('img');
  img.className = (live?.className ?? 'r-img product-media__image').replace(/\br-img--reveal\b/g, '').replace(/\s+/g, ' ').trim();
  img.alt = '';
  img.decoding = 'async';
  img.loading = 'eager';
  const width = host.getAttribute('data-r-hover-width');
  const height = host.getAttribute('data-r-hover-height');
  if (width && height) {
    img.width = Number(width);
    img.height = Number(height);
  }
  const position = host.getAttribute('data-r-hover-position');
  if (position) img.style.objectPosition = position;
  if (widths.length) {
    img.srcset = widths.map((w) => `${url}${join}width=${w} ${w}w`).join(', ');
    const sizes = live?.getAttribute('sizes');
    if (sizes) img.sizes = sizes.replace(/^auto,\s*/, '');
    img.src = `${url}${join}width=${widths[widths.length - 1]}`;
  } else {
    img.src = `${url}${join}width=${live?.width || 800}`;
  }
  host.replaceChildren(img);
}

/**
 * @param {Element} root
 */
function buildHoverImagesWithin(root) {
  if (!canHover?.matches) return;
  for (const host of root.querySelectorAll(HOVER_HOST)) buildHoverImage(host);
}

/**
 * Intent. Scoped to `product-card` as well as `slideshow-component` so that entering a
 * card anywhere — its title, its price, its swatches — warms the gallery, which usually
 * buys a few hundred milliseconds over waiting for the cursor to reach the image itself.
 * @param {Event} event
 */
function onIntent(event) {
  const target = event.target;
  if (!(target instanceof Element)) return;

  const host = target.closest('product-card, product-card-link, slideshow-component');
  if (!host) return;
  hydrateWithin(host);
  // The hover overlay sits beside the slideshow, not inside it, so entering the image
  // (whose nearest host IS the slideshow) has to look up to the card.
  if (event.type === 'pointerover' || event.type === 'focusin')
    buildHoverImagesWithin(host.closest('product-card, product-card-link') ?? host);
}

for (const type of ['pointerover', 'touchstart', 'focusin']) {
  document.addEventListener(type, onIntent, { capture: true, passive: true });
}

/* ↗ .cursor/references/code-notes/r-slide-media.js.md#§4 */
if ('MutationObserver' in window) {
  new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      if (mutation.oldValue !== 'true') continue;

      const target = mutation.target;
      if (!(target instanceof Element) || target.localName !== SLIDE) continue;
      if (target.getAttribute('aria-hidden') !== 'false') continue;

      hydrateNeighbourhood(target);
    }
  }).observe(document.documentElement, {
    subtree: true,
    attributes: true,
    attributeOldValue: true,
    attributeFilter: ['aria-hidden'],
  });
}

/* Drag. `scroll` does not bubble, so this only sees it during capture. rAF-coalesced because a drag fires it at
   frame rate and there is no point re-checking the same slide sixty times a second. */
let scrollFrame = 0;
/** @type {Set<Element>} */
const pendingScrollers = new Set();

document.addEventListener(
  'scroll',
  (event) => {
    const target = event.target;
    if (!(target instanceof Element) || target.localName !== SCROLLER) return;

    pendingScrollers.add(target);
    if (scrollFrame) return;

    scrollFrame = requestAnimationFrame(() => {
      scrollFrame = 0;
      for (const scroller of pendingScrollers) {
        if (scroller.isConnected) hydrateNeighbourhood(activeSlide(scroller));
      }
      pendingScrollers.clear();
    });
  },
  { capture: true, passive: true }
);
